import { deburr } from 'lodash-es';

import { SelectedFilter } from '~constants/enums';

export type FilterCategory = {
  filter: SelectedFilter;
  label: string;
};

const NO_MATCH_SCORE = 0;
const EXACT_MATCH_SCORE = 4;
const PREFIX_MATCH_SCORE = 3;
const WORD_PREFIX_MATCH_SCORE = 2;
const SUBSTRING_MATCH_SCORE = 1;

// An `@Category:` command only drills in when the text before the colon matches a
// category by exact/prefix/word-start. A loose substring match is too aggressive to
// trigger an automatic drill on a single keystroke.
const COMMAND_MIN_SCORE = WORD_PREFIX_MATCH_SCORE;

// Strips accents and casing so "Foremen" matches "foremen" and "Führer" matches "fuhrer".
function normalize(value: string) {
  return deburr(value).toLowerCase().trim();
}

function scoreCategory(normalizedLabel: string, normalizedQuery: string) {
  if (normalizedLabel === normalizedQuery) {
    return EXACT_MATCH_SCORE;
  }

  // Within a match tier, reward labels the query more completely fills. This breaks the
  // "driver" tie in favour of "Drivers" (fills 6/7) over "Driver Rates" (fills 6/12),
  // and stays below the next tier because the query is always shorter than the label here.
  const closeness = normalizedQuery.length / normalizedLabel.length;

  if (normalizedLabel.startsWith(normalizedQuery)) {
    return PREFIX_MATCH_SCORE + closeness;
  }

  const matchesWordStart = normalizedLabel
    .split(' ')
    .some((word) => word.startsWith(normalizedQuery));

  if (matchesWordStart) {
    return WORD_PREFIX_MATCH_SCORE + closeness;
  }

  if (normalizedLabel.includes(normalizedQuery)) {
    return SUBSTRING_MATCH_SCORE + closeness;
  }

  return NO_MATCH_SCORE;
}

// Array.prototype.sort is stable, so categories that tie on score keep their incoming
// (enum/alphabetical) order.
function scoreCategories(categories: FilterCategory[], normalizedQuery: string) {
  return categories
    .map((category) => ({
      category,
      score: scoreCategory(normalize(category.label), normalizedQuery),
    }))
    .filter((entry) => entry.score > NO_MATCH_SCORE)
    .sort((a, b) => b.score - a.score);
}

export function rankFilterCategories(categories: FilterCategory[], query: string) {
  const normalizedQuery = normalize(query);

  if (!normalizedQuery) {
    return categories;
  }

  return scoreCategories(categories, normalizedQuery).map((entry) => entry.category);
}

// Parses a fast-path command like "@Customer: acme" or "Customer:" into the target
// category plus the trailing option-search text. Returns null when there is no colon,
// no text before the colon, or the prefix does not confidently match a category.
export function parseFilterCommand(input: string, categories: FilterCategory[]) {
  const commandMatch = input.trimStart().match(/^@?([^:]+):(.*)$/);

  if (!commandMatch) {
    return null;
  }

  const [, rawPrefix, rawRemainder] = commandMatch;
  const normalizedPrefix = normalize(rawPrefix);

  if (!normalizedPrefix) {
    return null;
  }

  const [bestMatch] = scoreCategories(categories, normalizedPrefix);

  if (!bestMatch || bestMatch.score < COMMAND_MIN_SCORE) {
    return null;
  }

  return { filter: bestMatch.category.filter, remainder: rawRemainder.trim() };
}
